import { FLOWS } from 'universal/ucs';
import { FLOW_STEPS } from '@/constants/flow-steps';

const flowName = FLOWS.XO_PPDC;
const { PPDC_UPSELL_XO } = FLOW_STEPS.XO_PPDC;


const xoPpdcUpsellPageTag = `${flowName}:${PPDC_UPSELL_XO}`;
const xoPpdcErrorPageTag = `${flowName}:error`;

/**
 * Enum for XO PPDC FPTI event names.
 * @enum {string}
 */
const XO_PPDC_EVENTS = {
  /** XO PPDC upsell screen is shown. */
  UPSELL_SCREEN_SHOWN: 'ucs_xo_ppdc_upsell_screen_shown',
  /** XO PPDC incentive screen is shown. */
  INCENTIVE_SCREEN_SHOWN: 'ucs_xo_ppdc_incentive_screen_shown',
  /** XO PPDC upsell error screen is shown. */
  UPSELL_ERROR_SCREEN_SHOWN: 'ucs_xo_ppdc_upsell_error_screen_shown',

  /** Direct access detected; not from Iframe */
  DIRECT_ACCESS_DETECTED: 'ucs_xo_ppdc_direct_access_detected',
  
  /** Mismatching offer IDs. */
  MISMATCHING_OFFER_IDS: 'ucs_xo_ppdc_mismatching_offer_ids',

  /** Get PPDC button is clicked on upsell screen. */
  GET_PPDC_CLICKED: 'ucs_xo_ppdc_get_card_pressed',
  /** Back/Cancel button is clicked on upsell screen. */
  UPSELL_BACK_CLICKED: 'ucs_xo_ppdc_upsell_back_pressed',

  /** Error screen CTA button is clicked. */
  ERROR_CTA_CLICKED: 'ucs_xo_ppdc_error_cta_pressed',

  /** User successfully enrolled in PPDC. */
  ENROLLMENT_SUCCESS: 'ucs_xo_ppdc_enrollment_success_occurred',
  /** Enrollment API error occurred. */
  ENROLLMENT_API_ERROR: 'ucs_xo_ppdc_enrollment_api_error_occurred',
  /** Eligibility check returned invalid/ineligible. */
  ELIGIBILITY_INVALID: 'ucs_xo_ppdc_eligibility_invalid',
  /** Application returned invalid/needs more data. */
  APPLICATION_INVALID: 'ucs_xo_ppdc_application_invalid',

  /** Enrollment LEAP start. */
  PPDC_LEAP_START: 'ucs_xo_ppdc_leap_start',
  /** Enrollment LEAP success. */
  PPDC_LEAP_SUCCESS: 'ucs_xo_ppdc_leap_success',
  /** Enrollment LEAP failure. */
  PPDC_LEAP_FAIL: 'ucs_xo_ppdc_leap_fail',

  /** User was redirected to Risk stepup auth flow. */
  STEPUP_REDIRECT_OCCURRED: 'ucs_xo_ppdc_stepup_redirect_occurred',
  /** User returned from stepup auth successfully. */
  STEPUP_SUCCESS_RETURNED: 'ucs_xo_ppdc_stepup_success_returned',
  /** User returned from stepup auth with failure. */
  STEPUP_FAILURE_RETURNED: 'ucs_xo_ppdc_stepup_failure_returned',
};

const UPSELL_SCREEN_TAGS = {
  upsellScreenImpressionTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.UPSELL_SCREEN_SHOWN,
  },
  incentiveScreenImpressionTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.INCENTIVE_SCREEN_SHOWN,
  },
  getPpdcClickTags: {
    page: xoPpdcUpsellPageTag,
    link: 'getPpdcButton',
    eventName: XO_PPDC_EVENTS.GET_PPDC_CLICKED,
  },
  upsellBackClickTags: {
    page: xoPpdcUpsellPageTag,
    link: 'backButton',
    eventName: XO_PPDC_EVENTS.UPSELL_BACK_CLICKED,
  },
  upsellErrorScreenImpressionTags: {
    page: xoPpdcErrorPageTag,
    eventName: XO_PPDC_EVENTS.UPSELL_ERROR_SCREEN_SHOWN,
  },
  mismatchingOfferIdTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.MISMATCHING_OFFER_IDS,
  },
  directAccessDetectedTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.DIRECT_ACCESS_DETECTED,
  },
};

const ERROR_SCREEN_TAGS = {
  enrollmentApiErrorActivityTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.ENROLLMENT_API_ERROR,
  },
  eligibilityInvalidActivityTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.ELIGIBILITY_INVALID,
  },
  applicationInvalidActivityTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.APPLICATION_INVALID,
  },
  errorCtaClickTags: {
    page: xoPpdcErrorPageTag,
    link: 'errorCtaButton',
    eventName: XO_PPDC_EVENTS.ERROR_CTA_CLICKED,
  },
};

const SUCCESS_TRACKING_TAGS = {
  enrollmentSuccessActivityTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.ENROLLMENT_SUCCESS,
  },
};

const LEAP_TRACKING_TAGS = {
  enrollmentLeapStartActivityTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.PPDC_LEAP_START,
  },
  enrollmentLeapSuccessActivityTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.PPDC_LEAP_SUCCESS,
  },
  enrollmentLeapFailActivityTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.PPDC_LEAP_FAIL,
  },
};


const STEPUP_TRACKING_TAGS = {
  stepupRedirectActivityTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.STEPUP_REDIRECT_OCCURRED,
  },
  stepupSuccessReturnActivityTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.STEPUP_SUCCESS_RETURNED,
  },
  stepupFailureReturnActivityTags: {
    page: xoPpdcUpsellPageTag,
    eventName: XO_PPDC_EVENTS.STEPUP_FAILURE_RETURNED,
  },
};

const XO_PPDC_FPTI_TAGS = {
  ...UPSELL_SCREEN_TAGS,
  ...ERROR_SCREEN_TAGS,
  ...SUCCESS_TRACKING_TAGS,
  ...LEAP_TRACKING_TAGS,
  ...STEPUP_TRACKING_TAGS,
};

export { XO_PPDC_EVENTS };
export default XO_PPDC_FPTI_TAGS; 